/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useRef } from "react"
import { PcgPlaybackFilterControls } from "@medaica/common/components/pcg-viewer/audio/use-pcg-playback-filter"
import { DEFAULT_CUSTOM_BAND } from "@medaica/common/components/pcg-viewer/audio/pcg-playback-filter"

type PresetName = "wide" | "low" | "high"

// Listening ranges (upstream v0.9.1): Low 35–200 Hz, High 150–1000 Hz,
// Wide 35–1200 Hz. Button order follows the template: None · Wide · Low · High.
const PRESET_BANDS: { [preset in PresetName]: [number, number] } = {
  wide: [35, 1200],
  low: [35, 200],
  high: [150, 1000],
}
// Each preset button's control tip (upstream v0.9.1 data-hover text)
const PRESET_TIPS: Record<string, string> = {
  wide:
    "Wide · 35–1200 Hz level-compensated playback band for general cardiac listening. " +
    "Playback and display only; stored WAV and calculated results are unchanged.",
  high:
    "High · 150–1000 Hz level-compensated playback band for murmurs, clicks, and S2 splits with the low thump " +
    "reduced. Playback and display only; stored WAV and calculated results are unchanged.",
  low:
    "Low · 35–200 Hz level-compensated playback band for S1/S2, S3/S4, and low rumbles. " +
    "Playback and display only; stored WAV and calculated results are unchanged.",
}
const PRESET_ORDER: PresetName[] = ["wide", "low", "high"]
const FILTER_MIN_HZ = 20
const FILTER_MAX_HZ = 2000
const FILTER_MIN_WIDTH_HZ = 10

const isPresetName = (value: string): value is PresetName => value in PRESET_BANDS

/**
 * Listening-filter block of the exam viewer's Audio row (handoff
 * `playbackFilterGroup` + `customFilterPanel`): four mutually exclusive
 * choices — None (unfiltered, unity-gain playback), Wide, Low, High — and the
 * playback-only custom band as a two-thumb range rail under a "Range"
 * heading (upstream v0.9.1). "None" is an explicit choice rather than a power
 * toggle, so the control always says what is being heard. Each band preset is
 * level-compensated by the filter graph; the tips say so.
 *
 * The filter itself lives in usePcgPlaybackFilter (page-lifetime preset,
 * spliced into the viewer's audio graph); this component only renders its
 * state. The last enabled band is remembered only so the (disabled) range
 * rail keeps showing it while no filter is applied.
 * Playback filters change listening only and never alter calculated results.
 */
const PcgFilterControls = ({ controls }: { controls: PcgPlaybackFilterControls }): ReactElement => {
  const enabled = controls.preset !== "off"
  const custom = controls.preset === "custom"
  // Last enabled band, shown on the disabled rail while no filter is applied.
  const memoryRef = useRef<{ preset: PresetName; band: [number, number]; custom: boolean }>({
    preset: "wide",
    band: PRESET_BANDS.wide,
    custom: false,
  })
  if (enabled) {
    memoryRef.current = custom
      ? { preset: memoryRef.current.preset, band: controls.customBand, custom: true }
      : {
          preset: controls.preset as PresetName,
          band: PRESET_BANDS[controls.preset as PresetName] ?? DEFAULT_CUSTOM_BAND,
          custom: false,
        }
  }
  const memory = memoryRef.current
  const [lowHz, highHz] = enabled
    ? custom
      ? controls.customBand
      : PRESET_BANDS[controls.preset as PresetName]
    : memory.band
  const activePreset: PresetName | null = enabled && !custom && isPresetName(controls.preset) ? controls.preset : null
  const titlePreset = enabled ? (custom ? "Custom" : controls.preset) : "none"
  const updateCustomRange = (changed: "low" | "high", value: number) => {
    let nextLow = changed === "low" ? value : lowHz
    let nextHigh = changed === "high" ? value : highHz
    if (changed === "low" && nextLow > nextHigh - FILTER_MIN_WIDTH_HZ) nextLow = nextHigh - FILTER_MIN_WIDTH_HZ
    if (changed === "high" && nextHigh < nextLow + FILTER_MIN_WIDTH_HZ) nextHigh = nextLow + FILTER_MIN_WIDTH_HZ
    controls.setCustomBand(nextLow, nextHigh)
  }

  const lowPct = (100 * (lowHz - FILTER_MIN_HZ)) / (FILTER_MAX_HZ - FILTER_MIN_HZ)
  const highPct = (100 * (highHz - FILTER_MIN_HZ)) / (FILTER_MAX_HZ - FILTER_MIN_HZ)
  const disabled = !controls.available

  return (
    <div className={`audio-filter-block${enabled ? "" : " bypassed"}`} aria-label="Playback filters">
      <div className="playback-filter-group">
        <span className="control-cluster-label">Filters</span>
        <button
          type="button"
          className={`playback-filter-btn filter-none-btn control-help${enabled ? "" : " on"}`}
          data-playback-filter="off"
          disabled={disabled}
          aria-pressed={!enabled}
          aria-label="No playback filter"
          data-hover="None · unfiltered unity-gain playback. Playback only; calculated results are unchanged."
          onClick={() => controls.setPreset("off")}
        >
          None
        </button>
        {PRESET_ORDER.map((preset) => (
          <button
            key={preset}
            type="button"
            className={`playback-filter-btn control-help${activePreset === preset ? " on" : ""}`}
            data-playback-filter={preset}
            disabled={disabled}
            aria-pressed={activePreset === preset}
            data-hover={PRESET_TIPS[preset]}
            onClick={() => controls.setPreset(preset)}
          >
            {preset[0].toUpperCase() + preset.slice(1)}
          </button>
        ))}
      </div>
      <section
        className={`custom-filter-panel${enabled ? "" : " bypassed"}`}
        aria-label="Playback-only custom frequency range"
        title={`${
          titlePreset[0].toUpperCase() + titlePreset.slice(1)
        } · ${lowHz}–${highHz} Hz · playback only, does not change calculated results`}
      >
        <div className="custom-filter-control">
          <div className="custom-filter-head">
            <span className="control-cluster-label custom-filter-heading">Range</span>
            <div className="custom-filter-values">
              <span>{lowHz} Hz</span>
              <span>{highHz} Hz</span>
            </div>
          </div>
          <div className="custom-filter-rail">
            <div className="custom-filter-track">
              <div
                className="custom-filter-selection"
                style={{ left: `${lowPct}%`, width: `${Math.max(0, highPct - lowPct)}%` }}
              />
            </div>
            <input
              className="custom-filter-range"
              type="range"
              min={FILTER_MIN_HZ}
              max={FILTER_MAX_HZ}
              step={5}
              value={lowHz}
              disabled={disabled || !enabled}
              aria-label="Playback filter lower frequency"
              onChange={(event) => updateCustomRange("low", Number(event.target.value))}
            />
            <input
              className="custom-filter-range"
              type="range"
              min={FILTER_MIN_HZ}
              max={FILTER_MAX_HZ}
              step={5}
              value={highHz}
              disabled={disabled || !enabled}
              aria-label="Playback filter upper frequency"
              onChange={(event) => updateCustomRange("high", Number(event.target.value))}
            />
          </div>
        </div>
      </section>
      {disabled ? (
        <span className="custom-filter-values">Playback filtering is unavailable in this browser.</span>
      ) : null}
    </div>
  )
}

export default PcgFilterControls
